import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceHistorySelectors } from '@/redux/selectors';
import { amlTraceHistoryActions } from '@/redux/actions';
import { AmlTraceListRequest } from '@/api';
import { AmlTraceHistoryItemData } from '@/redux/models';

interface UseLocateTraceByUuidResult {
  item: AmlTraceHistoryItemData | null;
  loading: boolean;
  notFound: boolean;
}

function useLocateTraceByUuid(
  uuid: string | undefined,
): UseLocateTraceByUuidResult {
  const dispatch = useAppDispatch();
  const { data: traceHistory, loading } = useAppSelector(
    amlTraceHistorySelectors.getTraceHistorySlice,
  );

  const item = useMemo(
    () => traceHistory?.items.find((i) => i.uuid === uuid) || null,
    [traceHistory, uuid],
  );

  const hasMorePages =
    !!traceHistory && traceHistory.totalCount - traceHistory.items.length > 0;
  const notFound = !item && !!traceHistory && !hasMorePages;

  useEffect(() => {
    if (item || loading) return;

    if (!traceHistory) {
      dispatch(amlTraceHistoryActions.fetchTraceHistory({}));
      return;
    }

    if (hasMorePages) {
      const currentPage = Math.ceil(
        traceHistory.items.length / traceHistory.pageLimit,
      );
      const payload: AmlTraceListRequest = { page: currentPage };
      dispatch(amlTraceHistoryActions.fetchTraceHistory(payload));
    }
  }, [item, loading, traceHistory, hasMorePages, dispatch]);

  return { item, loading, notFound };
}

export default useLocateTraceByUuid;
